import identity from 'lodash/identity'
import { configure } from 'mobx'
import { enableStaticRendering } from 'mobx-react-lite'
import type { AppContext, AppProps } from 'next/app'
import BaseApp from 'next/app'
import NProgress from 'nprogress'
import React, { useEffect, useRef, useState } from 'react'
import { Provider } from 'urql'

import { Layout } from '@components/layout'
import { DefaultMeta } from '@contentful-components/page-meta/default-meta'
import type { ContentfulClient } from '@graphql/client'
import { getOrCreateContentfulClient } from '@graphql/client'
import analyticsService from '@lib/analytics/AnalyticsService'
import { PageError } from '@lib/errors/PageError'
import { useCookieConsent } from '@lib/hooks/useCookieConsent'
import { useEffectAsync } from '@lib/hooks/useEffectAsync'
import { useFirstInteraction } from '@lib/hooks/useFirstInteraction'
import { Intercom } from '@lib/intercom'
import type { RootStoreState } from '@lib/store/RootStore'
import { RootStore } from '@lib/store/RootStore'
import { RootStoreProvider } from '@lib/store/useStores'
import { UIContextProvider } from '@lib/store/useUIStore'
import type { PageWithContext } from '@lib/utils/context'
import { isDocumentContext } from '@lib/utils/context'
import { initSecurityMonitoring } from '@lib/utils/security-monitor'
import { getOrConfigureTeamEnvironment } from '@lib/utils/team-env'
import { isBrowser } from '@shared/util/env'
import 'normalize.css/normalize.css'
import 'nprogress/nprogress.css'
import '@fontsource-variable/inter'
import '../styles/index.scss'

// TODO (@cataline): Remove this
configure({ enforceActions: 'never' })

NProgress.configure({ showSpinner: false })

// Suppress layoutEffect warnings for SSR
if (!isBrowser()) {
    React.useLayoutEffect = React.useEffect
    enableStaticRendering(true)
}

const App = ({
    Component,
    pageProps,
    rootStore,
    graphQLClient,
    router,
    asPath,
}: Props) => {
    useFirstInteraction(Intercom.boot)

    useEffect(() => {
        router.events.on('routeChangeStart', () => NProgress.start())
        router.events.on('routeChangeComplete', () => NProgress.done())
        router.events.on('routeChangeError', () => NProgress.done())
    }, [router])

    useEffect(() => {
        initSecurityMonitoring()
    }, [])

    useCookieConsent()

    const lastTrackedPath = useRef<string | null>(null)

    // Initialize analytics service
    useEffectAsync(async () => {
        // Safely convert query params for URLSearchParams
        const queryEntries: Array<[string, string]> = []
        Object.entries(router.query).forEach(([key, value]) => {
            if (value !== undefined) {
                const stringValue = Array.isArray(value)
                    ? value.join(',')
                    : value
                queryEntries.push([key, stringValue])
            }
        })

        const searchParams = new URLSearchParams(queryEntries)
        await analyticsService.initialize(searchParams)
    }, [])

    // Track page views
    useEffectAsync(async () => {
        const pathName = router.asPath
        if (!pathName) return

        // Skip if it's the same path we already tracked
        if (pathName === lastTrackedPath.current) return

        // Safely convert query params to string record
        const queryParams: Record<string, string> = {}
        Object.entries(router.query).forEach(([key, value]) => {
            if (value !== undefined) {
                queryParams[key] = Array.isArray(value)
                    ? value.join(',')
                    : value
            }
        })

        await analyticsService.trackPageView({
            pathname: pathName,
            queryParams,
            fullUrl: pathName,
        })
        lastTrackedPath.current = pathName
    }, [router.asPath])

    const [graphql] = useState(() =>
        graphQLClient && 'ssr' in graphQLClient
            ? graphQLClient
            : getOrCreateContentfulClient(router.asPath, graphQLClient),
    )

    const getLayout = Component.getLayout ? Component.getLayout : identity

    return (
        <RootStoreProvider value={rootStore}>
            {/* To make useQuery hook work, we need to pass graphQLClient to the graphqlProvider. */}
            <Provider value={graphql}>
                <UIContextProvider>
                    <DefaultMeta />
                    <Layout
                        asPath={asPath}
                        theme={pageProps?.data?.theme}
                        announcement={pageProps?.data?.announcement}
                        disclaimers={
                            pageProps?.data?.disclaimers ??
                            pageProps?.article?.disclaimers
                        }
                    >
                        {getLayout(<Component {...pageProps} />, pageProps)}
                    </Layout>
                </UIContextProvider>
            </Provider>
        </RootStoreProvider>
    )
}

App.getInitialProps = async (context: AppContext) => {
    const pageContext = context.ctx

    // if this is run within getServerSideProps, or API requests
    // we don't need to render anything and can just return
    if (!isDocumentContext(pageContext)) {
        return { props: {} }
    }

    const { req, res, query } = pageContext
    const { router } = context
    const asPath = req?.url ?? router.asPath

    const teamEnvironment = getOrConfigureTeamEnvironment(pageContext)

    const graphQLClient = getOrCreateContentfulClient(asPath)

    const rootStore = req
        ? RootStore.fromRequest(req, teamEnvironment)
        : RootStore.fromState({})

    const props = { pageProps: {}, rootStore, graphQLClient, asPath }

    await PageError.try({ res, query, router }, async () => {
        // this executes all the .getInitialProps() of the pages in the app
        Object.assign(props, await BaseApp.getInitialProps(context))
    })

    if (res) {
        const initialState = graphQLClient.ssr.extractData()
        return { ...props, graphQLClient: initialState }
    }

    return props
}

type Props = AppProps & {
    Component: PageWithContext
    rootStore: RootStore | RootStoreState
    graphQLClient: ContentfulClient | object
    asPath: string
}

export default App
